import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useSession } from '../../state/useSession';
import { chatStore } from '@/storage/projectStorePort';
import type { ChatMessage } from '@/llm/types';
import type { AppDefinition } from '@/types/app';
// Pure: the order an edit — and an undo — is saved and published in, and the words for each
// outcome. The phone's, so the two clients say the same things.
import {
  UNDO_LAST_CHANGE,
  UNDONE,
  UNDONE_BUT_OFFLINE,
  editReply,
  runUndo,
  runWebAppEdit,
  undoConfirmation,
} from '@/webapp/chatEdit';
// Pure: the earlier versions an edit keeps. Shared with the phone.
import { lastVersion } from '@/webapp/editHistory';
// Where the web's owner edits a list's live rows: its Your data tab, not the phone's Manage.
import { WEB_LIVE_PLACE, photoRowsToAdd, spokenList } from '@/webapp/listEdits';
import { addManagedRow } from '../../api/managedRows';
import { editApp } from '../../api/build';
import { askAboutSite, liveRowsForAsk } from '../../api/ask';
import { ApiError } from '../../api/client';
import { uploadAllMedia } from '../../api/media';
import type { MediaAsset } from '../../api/assets';
import {
  CHANGED_MEANWHILE,
  publishLatest,
  saveSource,
  saveUndo,
  storedDefinition,
  storedForEdit,
} from '../../state/livePage';
import { useProjects } from '../../state/projects';
import { Button } from '../../ui';
import { funnelStep } from '../../analytics/funnel';
import { ACCEPT, documentsOf, isPhoto, readPicks, type PickDeps, type Picked } from './attachments';

/** Said where a photo is added to an app that has no catalogue id to keep it under yet. */
const PUBLISH_FIRST = 'Publish your site first to add photos.';

/** Said when a question is asked about a site that has no catalogue id — nothing live to read. */
const ASK_PUBLISH_FIRST = 'Publish your site first to ask about it.';

/**
 * What a message does: Change edits the site and is charged; Ask answers a question about it,
 * free, and changes nothing (the owner's decision D4, 2026-09-30). Chat only ever edited, so
 * "How many people RSVP'd?" was charged as a change to the page (F26, deep-dive 2026-09-30).
 */
type Mode = 'change' | 'ask';


/** Every photo attached to a change failed to upload: nothing is asked for, so nothing is charged. */
function noneUploaded(failed: string[]): string {
  return `None of the photos uploaded (${failed.join(', ')}), so nothing was changed and you were not charged. Send it again to try once more.`;
}

/** Some did: the change was made with the rest, and the reply says which were left out. */
function droppedPhotos(failed: string[]): string {
  const one = failed.length === 1;
  return `${failed.length} photo${one ? '' : 's'} did not upload (${failed.join(', ')}), so the change was made without ${one ? 'it' : 'them'}.`;
}

/**
 * Rows a change wrote into a live list for the photos that came with it, offered to that list in
 * one tap (F10). `rows` are the ones not added yet — after a partial failure, the rest.
 */
interface PhotoOffer {
  appId: string;
  list: string;
  rows: Record<string, unknown>[];
  /** How many the offer started with, for "Added 1 of 2". */
  total: number;
  state: 'offered' | 'adding' | 'added';
  /** What went wrong with the last try, said under the button that tries again. */
  problem?: string;
}

const photoCount = (n: number) => `${n} photo${n === 1 ? '' : 's'}`;

/** The button's words: every photo the first time, "the rest" after some were added. */
function offerTitle(offer: PhotoOffer): string {
  const where = `your ${spokenList(offer.list)}`;
  if (offer.rows.length === offer.total) return `Add ${offer.total === 1 ? 'this photo' : `these ${photoCount(offer.total)}`} to ${where}`;
  return `Add the other ${offer.rows.length === 1 ? 'photo' : photoCount(offer.rows.length)} to ${where}`;
}

/** "12 KB" — a text file's size on its tile. */
function sizeLabel(bytes: number): string {
  return bytes < 1000 ? `${bytes} B` : `${Math.round(bytes / 1000)} KB`;
}

/**
 * A paperclip, drawn here: the shared icon registry (src/theme/icons.ts) has none, and adding
 * one there is a change to both clients for a glyph only this composer uses.
 */
function Paperclip() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M21 11.5l-8.6 8.6a5.5 5.5 0 01-7.8-7.8l9.2-9.2a3.7 3.7 0 015.2 5.2l-9.2 9.2a1.8 1.8 0 01-2.6-2.6l8.5-8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One file waiting in the composer. A photo's thumbnail is an object URL made from the JPEG it
 * became, and let go when the tile goes — taken off, sent, or the panel closed — since one leaks
 * per photo otherwise and an owner adding a carousel adds several.
 */
function AttachmentTile({ item, onRemove }: { item: Picked; onRemove: () => void }) {
  const file = item.kind === 'photo' ? item.file : null;
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setThumbnail(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <li className="chat-tile">
      {item.kind === 'photo'
        ? thumbnail && <img className="chat-tile-thumb" src={thumbnail} alt={item.name} />
        : <span className="chat-tile-thumb chat-tile-doc" aria-hidden="true">TXT</span>}
      <span className="chat-tile-text">
        <span className="type-caption chat-tile-name">{item.name}</span>
        {item.kind === 'text' && <span className="type-caption chat-tile-size">{sizeLabel(item.size)}</span>}
      </span>
      <button type="button" className="chat-tile-remove" aria-label={`Remove ${item.name}`} onClick={onRemove}>
        <span aria-hidden="true">×</span>
      </button>
    </li>
  );
}

/**
 * Why the live page was not updated, in the words that can help.
 *
 * The shared edit flow ends any failure it is not told about with "publish again when you have
 * a connection". That is true of exactly one kind: a request that got no answer. Everything the
 * server or this client DECIDED — a takedown, a page over 300KB, a session that expired, an app
 * with no id — is marked `refused`, so the reply is "Saved, but not live: <that sentence>" and
 * nobody is sent to check a connection that was fine. Chat edits publish themselves, so these
 * are no longer rare: every one of them now ends in this reply.
 *
 * A takedown's sentence is the body's `error`; the 403's own message says only "You do not have
 * access to that."
 */
function publishFailure(err: unknown): unknown {
  if (err instanceof ApiError && (err.kind === 'offline' || err.kind === 'timeout')) return err;
  const sentence = err instanceof ApiError && (err.code === 'taken_down' || err.takenDown === true)
    ? err.reason?.trim() || err.message
    : err instanceof Error ? err.message : '';
  return Object.assign(new Error(sentence), { refused: true as const });
}

/** `crypto.randomUUID` is not available everywhere this runs; these two always are. */
const newEditKey = () => `edit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * `**bold**`, and nothing else.
 *
 * The build's own summary named the app in bold, and printed as text it read "I built **Shot
 * Tracker Golf**". It is plain text now, but messages already stored say it that way, so this is
 * fixed where they are shown. React nodes, never HTML: a message can quote whatever somebody
 * typed.
 */
function withBold(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <Fragment key={i}>{part}</Fragment>,
  );
}

/**
 * Chat drives refinement. The instruction stays in the box until the change succeeds:
 * losing what someone typed because the model failed is the worst possible response to
 * a failure they did not cause.
 *
 * A change that succeeds goes live by itself, as it does on the phone: saved first, then
 * published as a CONTENT update — a page its owner took offline stays offline, a password page
 * stays behind its password — and the reply says which happened, or that the page was not
 * updated and why. Until now the web saved the change and said "the preview has the change",
 * and the page people actually open stayed as it was until somebody found "Publish changes".
 *
 * Photos and text files go with a change (the owner's carousel, 2026-09-29): the photos are
 * uploaded FIRST, one at a time, as the app's creator media, then the edit is asked with their
 * names; the files stay in the composer with the words until the change is made.
 */
export default function ChatPanel({
  projectId,
  appId,
  goal,
  source,
  onBusyChange,
  prefill,
  onOpenSettings,
  toJpeg,
}: {
  projectId: string;
  /**
   * The app's own id, which its page is assembled under. Checked before an edit is paid for:
   * an app without one could be charged for a change that can never reach its page.
   */
  appId: string;
  /**
   * Not read any more: an edit no longer assembles the page here — the publish after it does,
   * under the name as stored. Optional so the editor can stop passing it.
   */
  appName?: string;
  goal: string;
  source: string;
  /** Told when an edit starts and ends, so nothing else publishes while it is on its way. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Words another part of the editor asks to have said here — "Add sign-in", say — put in the
   * box for the owner to read and send, never sent for them: a message costs credits and changes
   * their app. A new `nonce` is a new request, so asking for the same words twice still works.
   * The phone's ChatView takes the same thing.
   */
  prefill?: { text: string; nonce: number } | null;
  /** Where the site is published from — offered when a photo is added to an app with no page yet. */
  onOpenSettings?: () => void;
  /** The browser's own decoder unless a test stands in for it: jsdom has none, and HEIC must fail on cue. */
  toJpeg?: PickDeps['toJpeg'];
}) {
  const { user } = useSession();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(false);
  /*
   * Always Change. Ask (D4, 2026-09-30) was taken out on the owner's word, 2026-10-01: "all we need
   * is the editing". The ask() path below is unreachable and kept only until the /ask route and
   * its client are removed together; nothing renders a way to choose it.
   */
  const mode = 'change' as Mode;
  /** A question on its way. Not `busy`: nothing publishes, so nothing else has to wait for it. */
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * The error above is a refusal for CREDITS. It said "You are out of credits." and nothing
   * else, in the editor — where the person most likely to buy credits is standing — with no way
   * to reach where they are bought. Read off the server's own code, which the shared edit flow
   * carries for exactly this.
   */
  const [outOfCredits, setOutOfCredits] = useState(false);
  const listEnd = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const undoDialog = useRef<HTMLDialogElement>(null);
  /** What Undo asks first, worked out from the record as stored when it was pressed. */
  const [undoAsk, setUndoAsk] = useState<{ title: string; body: string; confirm: string } | null>(null);
  /*
   * The request key of the change on its way, or of one whose answer never came. An edit is
   * charged before it runs, so a resend of the SAME words for the SAME source carries the SAME
   * key and the server can hand back what the first attempt made rather than charge again — the
   * phone's rule (`chatStore`). Anything else is a new change; and once one is applied it is
   * forgotten, so the same words sent after an undo are made again, not replayed.
   *
   * The files are part of the change: `attachments` is which picks went with it, so the same
   * words with other photos are a new change and not the old answer replayed.
   */
  const pendingEdit = useRef<{ key: string; source: string; instruction: string; attachments: string } | null>(null);
  /*
   * Photos already uploaded for the picks in the composer. A resend after a lost answer — or
   * after CHANGED_MEANWHILE, whose resend is a new change on a new source — sends the photos
   * already in storage rather than putting the same bytes there twice.
   */
  const uploads = useRef<{ signature: string; appId: string; uploaded: MediaAsset[]; failed: string[] } | null>(null);
  /** Files waiting in the composer, and what was said about the ones that could not be added. */
  const [picked, setPicked] = useState<Picked[]>([]);
  const pickedNow = useRef(picked);
  pickedNow.current = picked;
  const [pickNotes, setPickNotes] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [needsPublish, setNeedsPublish] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  /*
   * The live page's address under each reply whose change reached it, by message id. Drawn as a
   * link by this screen from the address the publish came back with — never kept in the message
   * as HTML: a message is text, and can quote whatever somebody typed.
   */
  const [liveLinks, setLiveLinks] = useState<Record<string, string>>({});
  /*
   * One-tap offers to put a change's photo rows into a live list, by the reply they belong to
   * (F10). Kept on screen only: an offer is about the photos of one message, and once the
   * conversation is reopened the rows can be added in Your data → Live like any others.
   */
  const [photoOffers, setPhotoOffers] = useState<Record<string, PhotoOffer[]>>({});
  /*
   * The version before the last change, from the record as this screen has it. `updateProject`
   * puts every write here — an edit's save, an undo — so the button follows the history rather
   * than being remembered separately and going stale.
   */
  const opened = useProjects((s) => (s.currentProject?.id === projectId ? s.currentProject.definition : null));
  const previous = opened ? lastVersion(opened as AppDefinition) : null;
  /** Photos are uploaded under the catalogue id, which only an app that has been published has. */
  const hostedAppId = (opened as AppDefinition | null)?.hostedAppId;
  const existingNames = ((opened as AppDefinition | null)?.assets ?? []).map((a) => a.id);

  /**
   * Picked, dropped or pasted: each photo becomes a JPEG with a name the page can use, each text
   * file is read — and what cannot be used is said now, never after the change was paid for.
   */
  async function addFiles(chosen: File[]): Promise<void> {
    /*
     * Only a photo needs the catalogue id — it is uploaded under it. A text file goes in the
     * /edit body itself, so a site never published takes one, and only its photos are left out
     * with the note that says where to publish.
     */
    const files = hostedAppId ? chosen : chosen.filter((f) => !isPhoto(f));
    setNeedsPublish(files.length < chosen.length);
    if (!files.length) return;
    setReading(true);
    setPickNotes([]);
    try {
      const { added, problems } = await readPicks(files, { picked: pickedNow.current, existing: existingNames }, { toJpeg });
      setPicked((p) => [...p, ...added]);
      setPickNotes(problems);
    } finally {
      setReading(false);
    }
  }

  // Opens whether or not the site is published: a text file needs no catalogue id, and a photo
  // picked for a site without one is said when it is picked (`addFiles`).
  function openPicker(): void {
    fileInput.current?.click();
  }

  // A new request replaces whatever was in the box, once — keyed on the nonce alone, since the
  // text of the same request is the same request.
  const prefillNonce = prefill?.nonce;
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    box.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillNonce]);

  /*
   * A file dropped anywhere else on the page — the top bar, the margins — is not opened in the
   * tab either, which would throw the editor away. Not added: only the chat takes files. A file
   * input keeps its own drop, which is how it is given files.
   */
  useEffect(() => {
    const keep = (e: DragEvent) => {
      if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
      if (e.target instanceof HTMLInputElement && e.target.type === 'file') return;
      e.preventDefault();
    };
    document.addEventListener('dragover', keep);
    document.addEventListener('drop', keep);
    return () => {
      document.removeEventListener('dragover', keep);
      document.removeEventListener('drop', keep);
    };
  }, []);

  useEffect(() => {
    void chatStore()
      .getByProjectId(projectId)
      .then(setMessages)
      .catch(() => setMessages([]));
  }, [projectId]);

  useEffect(() => {
    listEnd.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, busy, asking]);

  /**
   * Ask: the rows the site's lists hold now, sent to /ask with the question — the server reads
   * the site itself from its published page — and the answer said in
   * the conversation, marked as an answer. Nothing is edited, saved or published, and nothing is
   * charged. The words stay in the box until they are answered, as a change's do.
   */
  async function ask(): Promise<void> {
    const question = text.trim();
    if (!question || busy || asking) return;
    setError(null);
    setOutOfCredits(false);
    setAsking(true);
    const ownerId = user?.id ?? '';
    try {
      const definition = await storedDefinition(projectId, ownerId);
      const catalogueId = definition.hostedAppId;
      if (!catalogueId) {
        setError(ASK_PUBLISH_FIRST);
        return;
      }
      const siteSource = definition.source ?? '';
      // The same words again, after they got no answer, are the same question: stored once.
      const last = messages[messages.length - 1];
      if (!(last?.role === 'user' && last.kind === 'question' && last.content === question)) {
        const asked = await chatStore().addMessage(projectId, {
          role: 'user',
          content: question,
          kind: 'question',
          timestamp: new Date().toISOString(),
        });
        setMessages((m) => [...m, asked]);
      }
      const rows = await liveRowsForAsk(catalogueId, siteSource);
      const answer = await askAboutSite({ appId: catalogueId, question, rows });
      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: answer,
        kind: 'answer',
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
      setText((t) => (t.trim() === question ? '' : t));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'That question could not be answered. Try again.');
    } finally {
      setAsking(false);
    }
  }

  async function send(): Promise<void> {
    const instruction = text.trim();
    if (!instruction || busy || asking || reading) return;
    // What goes with THIS change: files added while it is on its way are the next one's.
    const attached = picked;
    const photos = attached.flatMap((p) => (p.kind === 'photo' ? [p] : []));
    const documents = documentsOf(attached);
    const signature = attached.map((p) => p.key).join(',');
    // The words, and under them the files that went with them — the owner's record of what was asked.
    const asked = attached.length ? `${instruction}\n\nAttached: ${attached.map((p) => p.name).join(', ')}` : instruction;
    setError(null);
    setOutOfCredits(false);
    setBusy(true);
    onBusyChange?.(true);
    const ownerId = user?.id ?? '';
    // The screen's copy is read when the project opens; this puts the stored one on screen.
    const refreshScreen = () => void useProjects.getState().loadProject(projectId, ownerId);
    const now = new Date().toISOString();
    try {
      /*
       * What the model is asked to change: the app as STORED, read before anything is paid for.
       * The `source` this screen was drawn with is only re-read when the project is opened, so
       * after another tab changed the app it was older than the store, and every send — the
       * resend CHANGED_MEANWHILE asks for included — paid for an edit the save then refused.
       */
      const current = await storedForEdit(projectId, ownerId);
      const sent = current.source;
      if (sent !== source) refreshScreen();
      /*
       * The same words again, after they got no answer, are the same request: the phone's rule
       * (`chatStore`). A failure leaves the words in the box AND their bubble in the list, so
       * Send used to show and store the message twice. Once anything answered it, the same
       * words are a new change.
       */
      const last = messages[messages.length - 1];
      if (!(last?.role === 'user' && last.kind !== 'question' && last.content === asked)) {
        const userMessage = await chatStore().addMessage(projectId, {
          role: 'user',
          content: asked,
          timestamp: now,
        });
        setMessages((m) => [...m, userMessage]);
      }

      /** The photos that reached storage for this change, and the ones that did not. */
      let uploaded: MediaAsset[] = [];
      let dropped: string[] = [];
      /** The live page's address, when the publish below says visitors can open it. */
      let liveAt: string | undefined;
      const outcome = await runWebAppEdit({
        source: sent,
        instruction,
        // The phone's goal: the project's description, else the app's own.
        goal: current.goal ?? (goal || undefined),
        livePlace: WEB_LIVE_PLACE,
        /*
         * /edit alone; the publish below assembles the saved source under the app's EXISTING
         * id. The plan goes with it, as it does from the phone: it is how the edit keeps the
         * rules, the limits and the collections the build set.
         */
        edit: async (input) => {
          /*
           * The photos go up FIRST, one at a time, as the app's creator media under its
           * catalogue id, so the edit can be told their names. Inside `edit`, so anything that
           * stops here is reported like a refused edit and leaves the words and files in place.
           */
          if (photos.length) {
            const catalogueId = current.hostedAppId;
            if (!catalogueId) throw new Error(PUBLISH_FIRST);
            const done = uploads.current?.signature === signature && uploads.current.appId === catalogueId
              ? uploads.current
              : {
                signature,
                appId: catalogueId,
                ...(await uploadAllMedia(
                  catalogueId,
                  photos.map((p) => ({ name: p.name, contentType: 'image/jpeg', file: p.file })),
                )),
              };
            /*
             * Not one arrived: asking for "use these as the backgrounds" without them is a charge
             * for a change that cannot be what was meant. Stop before the edit, and try the
             * uploads again on the next Send.
             */
            if (!done.uploaded.length) {
              uploads.current = null;
              throw new Error(noneUploaded(done.failed));
            }
            uploads.current = done;
            ({ uploaded, failed: dropped } = done);
          }
          const pending = pendingEdit.current;
          const requestKey = pending && pending.source === sent && pending.instruction === instruction
            && pending.attachments === signature
            ? pending.key
            : newEditKey();
          pendingEdit.current = { key: requestKey, source: sent, instruction, attachments: signature };
          /*
           * The shared POST /edit contract: the names of the photos that came with this change,
           * the names of the ones the app already has (a new one under an old name replaces it),
           * and the text files. Each only when there are some.
           *
           * The site's own names only with a change that carries files: absent means the edit
           * there was before files, and the server writes a prompt section for `existingMedia`
           * alone — sent always, it changed the prompt of every plain edit of a site with photos.
           */
          const media = uploaded.map((a) => a.id);
          const carriesFiles = photos.length > 0 || documents.length > 0;
          const existingMedia = carriesFiles
            ? (current.assets ?? []).map((a) => a.id).filter((name) => !media.includes(name))
            : [];
          const result = await editApp({
            appId,
            ...input,
            plan: current.plan,
            requestKey,
            ...(media.length ? { media } : {}),
            ...(existingMedia.length ? { existingMedia } : {}),
            ...(documents.length ? { documents } : {}),
          });
          // Only the code and the drift report are read; the counters are the server's own.
          return { appCode: result.appCode, attempts: 0, gateFailures: 0, ...(result.drift ? { drift: result.drift } : {}) };
        },
        /*
         * The version it replaces is kept with it, so Undo last change can bring it back — and
         * the photos it was made with are saved in the same write, so the publish below sends
         * them with the page that names them.
         */
        save: (next) =>
          saveSource(projectId, ownerId, sent, next, instruction, uploaded.length ? { assets: uploaded } : {}).catch((err: unknown) => {
            // Refused because the stored app moved on: show that version, the one a resend edits.
            refreshScreen();
            throw err;
          }),
        publish: async () => {
          const published = await publishLatest(projectId, ownerId).catch((err: unknown) => {
            throw publishFailure(err);
          });
          if (published.webPublished && /^https?:\/\//.test(published.siteUrl)) liveAt = published.siteUrl;
          return { offline: !published.webPublished };
        },
      });

      if (outcome.status === 'failed') {
        // Nothing changed, so what they typed stays in the box.
        setError(outcome.message);
        setOutOfCredits(outcome.reason === 'insufficient_credits');
        return;
      }
      // Applied: the same words sent again are a new change, not this one replayed.
      pendingEdit.current = null;
      uploads.current = null;

      /*
       * The phone's words, from the one function that decides them. This put its own "Done — your
       * live page is updated." over the outcome's note, so an edit that changed only a live list's
       * starting rows still opened with "Done" (the café, 2026-09-29). See `editReply`. A photo
       * that did not upload was left out of the change, and that is said here too.
       */
      const said = [editReply(outcome), dropped.length ? droppedPhotos(dropped) : ''].filter(Boolean).join('\n\n');
      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: said,
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
      // The way to go and look, under the reply — only when the change is on a page visitors can open.
      const openAt = outcome.status === 'updated' && !outcome.offline ? liveAt : undefined;
      if (openAt) setLiveLinks((links) => ({ ...links, [reply.id]: openAt }));
      /*
       * The photos of THIS message that the change wrote into a live list as starting rows —
       * which visitors never see (F10). Offered, never written for the owner: only on a change
       * that reached the page, so the list is declared on the server to be written to.
       */
      if (outcome.status === 'updated' && uploaded.length && current.hostedAppId) {
        const catalogueId = current.hostedAppId;
        const offers = photoRowsToAdd(sent, outcome.source, uploaded.map((a) => a.id)).map((o): PhotoOffer => ({
          appId: catalogueId,
          list: o.list,
          rows: o.rows,
          total: o.rows.length,
          state: 'offered',
        }));
        if (offers.length) setPhotoOffers((all) => ({ ...all, [reply.id]: offers }));
      }
      /*
       * Saved either way, so the instruction has been used — but only IT is cleared, and only
       * the files that went with it. The box stays usable while an edit is on its way, and words
       * or files put there meanwhile are the next request, not this one.
       */
      setText((t) => (t.trim() === instruction ? '' : t));
      const sentKeys = new Set(attached.map((p) => p.key));
      setPicked((p) => p.filter((item) => !sentKeys.has(item.key)));
      setPickNotes([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That change could not be made.');
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }

  /**
   * Add an offer's rows to its live list, one at a time, through the same route Your data → Live
   * adds a row with. Only ADDS: the rows already in the list are never read or rewritten. A row
   * that fails stops the run, and the ones not yet added stay offered — so trying again never
   * adds a photo twice.
   */
  async function addPhotoRows(replyId: string, index: number): Promise<void> {
    const offer = photoOffers[replyId]?.[index];
    if (!offer || offer.state !== 'offered') return;
    const set = (next: Partial<PhotoOffer>) =>
      setPhotoOffers((all) => ({
        ...all,
        [replyId]: (all[replyId] ?? []).map((o, i) => (i === index ? { ...o, ...next } : o)),
      }));
    set({ state: 'adding', problem: undefined });
    const left = [...offer.rows];
    try {
      while (left.length) {
        await addManagedRow(offer.appId, offer.list, left[0]);
        left.shift();
      }
      set({ state: 'added', rows: [] });
    } catch (err) {
      const added = offer.total - left.length;
      const why = err instanceof Error && err.message ? err.message : 'That did not go through.';
      set({ state: 'offered', rows: left, problem: `Added ${added} of ${photoCount(offer.total)}. ${why}` });
    }
  }

  /**
   * Undo last change, asked first — the phone's question, in its words (`undoConfirmation`): it
   * acts at once on a page other people may be using, and the change it throws away may have
   * been paid for. There is no redo. Worked out from the record as STORED, so it names the change
   * that will actually be undone even if another tab made one since this screen was drawn.
   */
  async function askUndo(): Promise<void> {
    if (busy) return;
    setError(null);
    setOutOfCredits(false);
    try {
      const ask = undoConfirmation(await storedDefinition(projectId, user?.id ?? ''));
      if (!ask) {
        // Undone elsewhere meanwhile: show what is stored, where there is nothing to take back.
        void useProjects.getState().loadProject(projectId, user?.id ?? '');
        return;
      }
      setUndoAsk(ask);
      undoDialog.current?.showModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be undone.');
    }
  }

  function closeUndo(): void {
    undoDialog.current?.close();
    setUndoAsk(null);
  }

  /**
   * Put back the version the last change replaced, and put THAT on the page — the phone's
   * `runUndo`: saved first, then published as a content update (a page taken offline stays
   * offline, a password page keeps its password), and said in the chat in the same words.
   * Busy like an edit, so nothing else publishes meanwhile. Only the version is restored, never
   * the chat: what was asked stays in the conversation, one Send away.
   */
  async function undo(): Promise<void> {
    closeUndo();
    if (busy) return;
    setError(null);
    setOutOfCredits(false);
    setBusy(true);
    setUndoing(true);
    onBusyChange?.(true);
    const ownerId = user?.id ?? '';
    const refreshScreen = () => void useProjects.getState().loadProject(projectId, ownerId);
    try {
      const from = await storedDefinition(projectId, ownerId);
      const outcome = await runUndo({
        definition: from,
        save: (restored) =>
          saveUndo(projectId, ownerId, from.source ?? '', restored.source ?? '').catch((err: unknown) => {
            // Moved on meanwhile: show the version as stored, the one a second Undo would act on.
            if (err instanceof Error && err.message === CHANGED_MEANWHILE) refreshScreen();
            throw err;
          }),
        publish: async () => {
          const published = await publishLatest(projectId, ownerId).catch((err: unknown) => {
            throw publishFailure(err);
          });
          return { offline: !published.webPublished };
        },
      });
      if (outcome.status === 'failed') {
        setError(outcome.message);
        return;
      }
      const said = outcome.status === 'saved_not_live'
        ? outcome.message
        : [outcome.offline ? UNDONE_BUT_OFFLINE : UNDONE, outcome.note].filter(Boolean).join('\n\n');
      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: said,
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be undone.');
    } finally {
      setBusy(false);
      setUndoing(false);
      onBusyChange?.(false);
    }
  }

  return (
    <div
      className="chat-panel"
      /*
       * Photos dragged anywhere on the chat are added as if picked — not only onto the composer:
       * a drop a little off it fell back to the browser's default, which opens the file in the
       * tab, and the editor went with the words and files waiting in it.
       */
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (!files.length) return;
        e.preventDefault();
        void addFiles(files);
      }}
    >
      {/*
        * A log, announced politely: a reply that lands 15-150 seconds after Send was never read
        * out, so a screen-reader user had no way to know the change was done (F25, deep-dive
        * 2026-09-30). Only <li> inside it — the scroll anchor used to be a <div> in the <ol>.
        *
        * The log WRAPS the list rather than being it: `<ol role="log">` replaced the list's role,
        * so every <li> lost its list parent — axe's `listitem` rule — and the F25 fix brought back
        * invalid list markup in a new form (review, 2026-09-30).
        */}
      <div className="chat-log" role="log" aria-live="polite" aria-label="Conversation">
        <ol className="chat-list">
          {messages.map((m) => (
            <li key={m.id} className={`chat-msg chat-${m.role}${m.kind ? ` chat-${m.kind}` : ''}`}>
              {/* A question and its answer are marked, so they read as that and not as a change made. */}
              {m.kind && <span className="type-caption chat-kind">{m.kind === 'answer' ? 'Answer' : 'Question'}</span>}
              <span className="type-body">{withBold(m.content)}</span>
              {liveLinks[m.id] && (
                <a
                  className="chat-open-site"
                  href={liveLinks[m.id]}
                  target="_blank"
                  rel="noreferrer"
                  // The funnel's last step, from the reply that says it is live (analytics/funnel.ts).
                  onClick={() => funnelStep('site_opened')}
                >
                  Open your site <span aria-hidden="true">↗</span>
                </a>
              )}
              {(photoOffers[m.id] ?? []).map((offer, i) => (
                <div key={offer.list} className="chat-offer">
                  {offer.state === 'added' ? (
                    <p className="type-caption" role="status">
                      {`Added ${photoCount(offer.total)} to your ${spokenList(offer.list)}. They are on your page now.`}
                    </p>
                  ) : (
                    <>
                      <Button
                        title={offerTitle(offer)}
                        variant="secondary"
                        loading={offer.state === 'adding'}
                        onPress={() => void addPhotoRows(m.id, i)}
                      />
                      {offer.problem && <p role="alert" className="type-caption field-error-text">{offer.problem}</p>}
                    </>
                  )}
                </div>
              ))}
            </li>
          ))}
          {/*
            * While a change is being made, the conversation says so: the spinner on Send was the only
            * sign of a 15-150 second edit (F25). Not stored — it is replaced by the reply.
            */}
          {busy && !undoing && (
            <li className="chat-msg chat-assistant chat-pending">
              <span className="type-body">Working on your change…</span>
            </li>
          )}
          {asking && (
            <li className="chat-msg chat-assistant chat-pending">
              <span className="type-body">Looking at your site…</span>
            </li>
          )}
        </ol>
      </div>
      <div ref={listEnd} />

      {/* Only when there is a version to go back to: a button that does nothing is a question. */}
      {previous && (
        <div className="build-actions">
          <Button
            title={UNDO_LAST_CHANGE}
            variant="ghost"
            loading={undoing}
            disabled={busy || asking}
            onPress={() => void askUndo()}
          />
        </div>
      )}

      <dialog
        ref={undoDialog}
        aria-label={undoAsk?.title ?? UNDO_LAST_CHANGE}
        className="confirm-dialog"
        onClose={() => setUndoAsk(null)}
      >
        {undoAsk && (
          <>
            <h2 className="type-title">{undoAsk.title}</h2>
            {undoAsk.body.split('\n\n').map((para) => (
              <p key={para} className="type-body">{para}</p>
            ))}
            <div className="build-actions">
              <Button title={undoAsk.confirm} variant="dangerSoft" onPress={() => void undo()} />
              <Button title="Cancel" variant="ghost" onPress={closeUndo} />
            </div>
          </>
        )}
      </dialog>

      <div className="chat-composer">
        <p className="type-caption chat-mode-note">A change uses credits and goes live on your page.</p>
        {/* Named for screen readers; on a phone the box's own "Describe a change…" says it, and the line is room. */}
        <label className="visually-hidden" htmlFor="instruction">
          {mode === 'change' ? 'What should I change?' : 'What do you want to know?'}
        </label>
        {/* Files go with a change; while asking they wait here, unsent, for the next one. */}
        {mode === 'ask' && picked.length > 0 && (
          <p className="type-caption chat-note">Your files go with a change, not a question. Switch to Change to send them.</p>
        )}
        {picked.length > 0 && (
          <ul className="chat-tiles" aria-label="Files to send">
            {picked.map((item) => (
              <AttachmentTile
                key={item.key}
                item={item}
                onRemove={() => setPicked((p) => p.filter((x) => x.key !== item.key))}
              />
            ))}
          </ul>
        )}
        <div className="chat-input-row">
          {mode === 'change' && (
            <button
              type="button"
              className="chat-attach"
              aria-label="Add photos or a text file"
              title="Add photos or a text file"
              disabled={reading}
              onClick={openPicker}
            >
              <Paperclip />
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="visually-hidden"
            tabIndex={-1}
            aria-label="Photos or text files to add"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              // So the same file can be picked again after it was taken off.
              e.target.value = '';
              void addFiles(files);
            }}
          />
          <textarea
            ref={box}
            id="instruction"
            className="field"
            rows={2}
            // The app exists: this box changes it or asks about it, and never describes a new one.
            placeholder={mode === 'change' ? 'Describe a change…' : 'Ask about your site or its replies…'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              // A screenshot pasted into the box is a photo to add, not text.
              const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
              if (!files.length) return;
              e.preventDefault();
              void addFiles(files);
            }}
            onKeyDown={(e) => {
              /*
               * Not the Enter that picks characters in a Japanese or Chinese IME: that sent the
               * message half written (F25). Safari reports it as keyCode 229 with isComposing
               * already false, so both are read.
               */
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void (mode === 'ask' ? ask() : send());
              }
            }}
          />
        </div>
        {reading && <p className="type-caption chat-note" role="status">Getting your files ready…</p>}
        {mode === 'change' && needsPublish && (
          <p className="type-caption chat-note">
            <span>{PUBLISH_FIRST}</span>{' '}
            {onOpenSettings && (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setNeedsPublish(false);
                  onOpenSettings();
                }}
              >
                Open Settings
              </button>
            )}
          </p>
        )}
        {mode === 'change' && pickNotes.length > 0 && (
          <div role="status">
            {pickNotes.map((note) => (
              <p key={note} className="type-caption field-error-text">{note}</p>
            ))}
          </div>
        )}
        {error && (
          <p role="alert" className="type-caption field-error-text">
            {error}
            {outOfCredits && (
              <>
                {' '}
                {/* A new tab: in this one it unmounted the editor, and the words and files the
                    failure kept in the composer went with it. */}
                <Link to="/credits" target="_blank" rel="noreferrer">Get credits</Link>
              </>
            )}
          </p>
        )}
        {mode === 'change' ? (
          <Button title="Send" onPress={() => void send()} loading={busy && !undoing} disabled={!text.trim() || undoing || reading || asking} />
        ) : (
          <Button title="Ask" onPress={() => void ask()} loading={asking} disabled={!text.trim() || busy} />
        )}
      </div>
    </div>
  );
}
